// entry-format.js -- the one place that decides how an audit entry's record,
// person, values, times and places read, and whether they link (spec §5;
// phase 4 ledger rulings on Titles, People and staff).
//
// The owner's principle: show current information only for what the reader
// can reach now; anything else shows as it was when they last had it, with
// no link. Staff are "EducationXR support" to customers.
import { COURSE_RECYCLE, courseHref, personHref, recordHref, recordNoun, recycleHref } from './record-links'
import { serverMessage } from '../error/server-message'
import { formatCount } from '../sso-workspace/format'
import { consentStatusLabel } from '../sso-consent/consent-model'
import { dateTimeText, utcText } from '../date/local-time'

export const SUPPORT_LABEL = 'EducationXR support'
export const LEFT_NOTE = 'No longer in this organization'
export const BUSY_TEXT = 'Busy, try again'

const STATUS_NOTE = {
  moved: 'Moved to another organization',
  deleted: 'Deleted',
  recycled: 'In the recycle bin'
}

const lc = s => String(s || '').trim().toLowerCase()

// Records. Ref as content ships it: {type, id, label, title, status,
// linkable}. status is current | recycled | moved | deleted, or empty when
// the type has no name column. Only current and recycled records link; the
// server's linkable=false always wins (staff records, masked join rows).
// Asset versions read "Version {versionCount}" with the filename (title)
// as secondary text -- content does not ship versionCount on a Ref yet, so
// without it the filename is the text.
// course: the caller knows this node is a course (a course launch), so it
// links to the course page. Those pages are in the signed-in organization,
// so for a super admin (sys), whose record may be another organization's,
// a course is text.
//
// A person record (users) links the way an actor does (personHref): a super
// admin opens the login, by the loginID content gives them. A join row
// (a role grant, a group membership: content's Ref.parts) reads as the
// records its key names, each one linked by these same rules.
export const recordView = (ref, { course = false, sys = false } = {}) => {
  if (!ref) return { text: '', secondary: null, href: null, note: null, linked: false, parts: null }
  const name = (ref.title || '').trim()
  // a type with its own noun here reads by it, before content's table label
  // ("AI tools session 31", never "Ai tools sessions 31")
  const fallback = [recordNoun(ref.type) || ref.label || 'Record', ref.id].filter(Boolean).join(' ')
  const status = ref.status || 'current'
  const reachable = ref.linkable !== false && (status === 'current' || status === 'recycled')
  const asCourse = course && ref.type === 'nodes'
  const page = ref.type === 'users' ? personHref({ userID: ref.id, loginID: ref.loginID }, sys) : recordHref(ref.type, ref.id)
  const href = !reachable || (asCourse && sys)
    ? null
    : asCourse
      ? (status === 'recycled' ? COURSE_RECYCLE : courseHref(ref.id))
      : status === 'recycled' ? recycleHref(ref.type) : page
  const versionCount = ref.versionCount ?? ref.version_count
  const isVersion = ref.type === 'asset_versions' && versionCount !== undefined && versionCount !== null && versionCount !== ''
  const parts = Array.isArray(ref.parts) && ref.parts.length > 0 ? ref.parts.map(p => recordView(p, { sys })) : null
  return {
    text: isVersion ? `Version ${versionCount}` : name || (parts ? parts.map(p => p.text).join(' · ') : fallback),
    secondary: isVersion ? name || null : null,
    href,
    note: STATUS_NOTE[status] || null,
    linked: Boolean(href),
    parts
  }
}

const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
// says: the sentence names text as a whole phrase, not inside a word
// ("gave Annabel ..." does not say "Ann").
const says = (said, text) => {
  const t = lc(text)
  return Boolean(t) && new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRe(t)}($|[^\\p{L}\\p{N}])`, 'u').test(said)
}

// The record as it follows an entry's sentence. Some sentences already name
// their record -- "added version 3" (asset.version.uploaded), "opened
// request <id>" (request_log.viewed) -- and the record after them repeated
// it: "added version 3 Version 3" (dev click-through 2026-10-04). Then the
// record shows only what the sentence doesn't say (an asset version's
// filename, still linked), or nothing. A record with a name of its own is
// always shown.
// A join row's parts the sentence already names are left out ("gave Jane
// Doe the owner role" is not followed by "owner · Jane Doe" again).
export const recordAfterSentence = (sentence, ref, opts) => {
  const v = recordView(ref, opts)
  const said = lc(sentence)
  if (!ref || !said || !v.text) return v
  if (v.parts) {
    const left = v.parts.filter(p => !says(said, p.text))
    return left.length === v.parts.length ? v : { ...v, parts: left, text: left.map(p => p.text).join(' · ') }
  }
  const versionSaid = ref.type === 'asset_versions' && v.text !== (ref.title || '').trim() && said.endsWith(lc(v.text))
  const idSaid = !(ref.title || '').trim() && ref.id && said.includes(lc(ref.id))
  if (!versionSaid && !idSaid) return v
  return { ...v, text: versionSaid ? v.secondary || '' : '', secondary: null }
}

export const initialsOf = text =>
  (text || '?')
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('') || '?'

const automationName = label => {
  const plain = String(label || '')
    .replace(/^(job|integration|migration|sso):/, '')
    .replace(/[-_]/g, ' ')
    .trim()
  return plain ? `Automatic: ${plain}` : 'Automatic change'
}

const plainActor = (text, extra) => ({
  text,
  email: null,
  href: null,
  note: null,
  initials: '?',
  automatic: false,
  unattributed: false,
  support: false,
  pivot: false,
  ...extra
})

// Staff, by either signal: the support flag or kind "support". Checked
// before anything reads a name, email or id, so an off-contract response
// can never surface a staff member to a customer.
const isStaff = actor => Boolean(actor && (actor.support || actor.kind === 'support'))

// People. Actor as content ships it: {kind, userID, loginID, label, name,
// email, status, linkable, automatic, unattributed, support}. A current
// person links; one who left shows as they were, with a note. Staff
// (support: true) read as EducationXR support: no link, no email, no person
// pivot. pivot says whether "everything this person did" is offered.
export const actorView = (actor, { sys = false } = {}) => {
  if (!actor) return plainActor('Unattributed change', { unattributed: true })
  if (isStaff(actor)) return plainActor(SUPPORT_LABEL, { initials: 'EX', support: true })
  // An integration (Phase D): an API key's write, which content names by
  // the key's service account ("<name> API key", audit-entries actorOf) and
  // marks automatic: false (a partner's change, shown by default), or an
  // automation such as a webhook (automatic: true). Either reads by its
  // name, never as a link or a person pivot: a key is no person to open.
  // The server's `automatic` decides the automatic chip and look, not the
  // kind.
  if (actor.kind === 'integration') {
    const name = (actor.name || '').trim()
    const automatic = actor.automatic === true
    return plainActor(name || automationName(actor.label), { initials: automatic || !name ? 'A' : initialsOf(name), automatic })
  }
  if (actor.automatic || actor.kind === 'system') {
    return plainActor(automationName(actor.label), {
      initials: 'A',
      automatic: true
    })
  }
  if (actor.unattributed && !actor.userID && !actor.loginID) return plainActor('Unattributed change', { unattributed: true })
  const name = (actor.name || '').trim()
  const email = actor.email || null
  const text = name || email || (actor.userID ? `Person ${actor.userID}` : actor.loginID ? `Account ${actor.loginID}` : 'Someone')
  const left = actor.status === 'left'
  const reachable = !left && actor.linkable !== false
  return {
    text,
    email,
    href: reachable ? personHref(actor, sys) : null,
    note: left ? LEFT_NOTE : null,
    initials: initialsOf(name || email),
    automatic: false,
    unattributed: false,
    support: false,
    pivot: reachable && Boolean(actor.userID || (sys && actor.loginID))
  }
}

// The "Only <person>" pivot: the actor facet key to filter on (user:<id>,
// login:<id>, auto:<label>), or null when there is none -- staff,
// unattributed changes and people who left are never pivots.
export const actorFilterKey = (actor, opts = {}) => {
  if (!actor || isStaff(actor)) return null
  const view = actorView(actor, opts)
  if (view.automatic) return actor.label ? `auto:${actor.label}` : null
  if (!view.pivot) return null
  if (actor.userID) return `user:${actor.userID}`
  return opts.sys && actor.loginID ? `login:${actor.loginID}` : null
}

// The person quick filter beside an entry's name (owner 2026-11-03): the
// same key as "Only <person>", for a person only (user:<id>, or a super
// admin's login:<id>). An automatic job or an integration is no person, so
// its name has no funnel (Details still offers "Only <job>").
export const personFilterKey = (actor, opts = {}) => {
  const key = actorFilterKey(actor, opts)
  return key && !key.startsWith('auto:') ? key : null
}

// How an action came to be made by that actor, or null: "(via <impersonator>)"
// for an impersonated action, and "via LMS connection <name>" for a learner's
// action in a bounded LMS session (Phase F F-11: core's via_connection_id,
// carried from the launch token's claim; content's entry names the connection
// as viaConnection {id, name}, and a connection since removed has no name).
export const viaText = (entry, opts = {}) => {
  if (!entry) return null
  if (entry.impersonator) return `(via ${actorView(entry.impersonator, opts).text})`
  const connection = entry.viaConnection
  if (connection || entry.viaConnectionID) {
    const name = connection && typeof connection.name === 'string' ? connection.name.trim() : ''
    return name ? `via LMS connection ${name}` : 'via an LMS connection'
  }
  return null
}

// "Sign out this session" (owner ruling 6): only for a reader of that
// organization's audit log whom /capabilities allows to sign sessions out
// (signOutSessions; missing = false), never while the reader is
// impersonating, and only for a person's own session -- never staff,
// impersonated, automatic or unattributed entries. Auth re-checks all of it
// (audit-api signOutSession).
export const canSignOutSession = (entry, caps) => {
  if (!entry || !entry.sessionID || !caps || !caps.canAudit) return false
  if (caps.signOutSessions !== true || caps.impersonating) return false
  if (entry.impersonator || !entry.actor || isStaff(entry.actor)) return false
  const who = actorView(entry.actor)
  return !who.automatic && !who.unattributed
}

// Who did it, as one line: an impersonated action is "<person> (via
// EducationXR support)" for a customer, or the impersonator's name for a
// super admin.
export const byline = (entry, opts = {}) => {
  if (!entry) return ''
  const who = actorView(entry.actor, opts).text
  const via = viaText(entry, opts)
  return via ? `${who} ${via}` : who
}

// Actor facet chips (GET /facets actors: {key, label, status, count}). The
// support and none buckets cannot be filtered on, so they cannot be
// clicked. A chip shows its own count, never "N results".
export const actorChip = facet => {
  const key = (facet && facet.key) || ''
  const count = (facet && facet.count) || 0
  const base = {
    key,
    count,
    countText: formatCount(count),
    note: null
  }
  if (key === 'support') return { ...base, text: SUPPORT_LABEL, clickable: false }
  if (key === 'none' || !key) return { ...base, text: 'Unattributed change', clickable: false }
  if (key.startsWith('auto:'))
    return {
      ...base,
      text: automationName(facet.label || key.slice(5)),
      clickable: true
    }
  const [kind, id] = key.split(':')
  const text = (facet.label || '').trim() || (kind === 'user' ? `Person ${id}` : `Account ${id}`)
  return {
    ...base,
    text,
    clickable: true,
    note: facet.status === 'left' ? LEFT_NOTE : null
  }
}

// POST /views refusals the screen never mentions: a bad body or a type
// that records no views (400), another organization's or an unknown record
// (404, one answer for both), a too-large body (413) and the per-minute
// budget (429). Anything else is a real failure.
export const isSilentViewError = err => Boolean(err) && [400, 404, 413, 429].includes(err.status)

const codeOf = err => (err && err.data && typeof err.data === 'object' ? err.data.code : undefined)

export const isCancelled = err => codeOf(err) === 'audit.cancelled'

// The sentence for a failed audit read. 503 audit.busy is a plain "Busy,
// try again"; a cancelled request (the reader moved on) says nothing (null);
// otherwise the server's sentence only when its body carries a code.
export const auditErrorText = (err, fallback) => {
  const code = codeOf(err)
  if (code === 'audit.cancelled') return null
  if (code === 'audit.busy') return BUSY_TEXT
  return serverMessage(err, fallback)
}

// Values by type (owner report 2026-10-07): content says what a field's
// value is (change.type: time, date, bool, number, enum, json, id, text,
// from the column's database type), and the value reads by it.

// A time as the page writes one for people: the viewer's own, with its
// zone ("Oct 14, 2026 at 3:20 PM MDT", shared/date/local-time); the exact
// UTC instant is its hover (exactUTC), as for the entry's own time.
export const localWhen = iso => dateTimeText(iso) || String(iso)

// A calendar date ("2026-10-14") is that day wherever the viewer is: never
// read as UTC midnight and shifted.
export const calendarDay = value => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!m) return String(value)
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Codes in words, by "table.column": the label maps the pages that own
// these values use (the consent model's statuses), and the words the SSO
// pages use for the rest. Any other code reads as its words ("at_risk" ->
// "At risk").
const CODE_WORDS = {
  'provider_consent_requests.status': consentStatusLabel,
  'provider_consent_requests.requested_via': { staff: 'EducationXR support', org: 'The organization' },
  'provider_organizations.join_policy': {
    everyone: 'Everyone who signs in',
    rule: 'Only people matching the rule',
    choose: 'People can choose to join',
    code: 'People with a join code'
  },
  'providers.driver': { oidc: 'OpenID Connect', apple: 'Apple', facebook: 'Facebook', microsoft: 'Microsoft' },
  'providers.connection': { own_registration: "Customer's own app registration", shared_app: 'EducationXR shared app' },
  'organization_deletions.source': { owner: 'The owner', sys_admin: 'EducationXR support' }
}

const codeWords = code => {
  const s = String(code).replace(/[_-]+/g, ' ').trim().toLowerCase()
  return s ? s[0].toUpperCase() + s.slice(1) : ''
}

export const codeText = (value, table, field) => {
  const words = CODE_WORDS[`${table}.${field}`]
  const named = typeof words === 'function' ? words(value) : words && words[value]
  return named && named !== value ? named : codeWords(value)
}

// c: the field row (its type, table and field), when the value is one.
export const valueText = (v, c = {}) => {
  if (v === null || v === undefined || v === '') return '(empty)'
  if (v === true || (c.type === 'bool' && v === 'true')) return 'Yes'
  if (v === false || (c.type === 'bool' && v === 'false')) return 'No'
  if (c.type === 'time' && typeof v === 'string') return localWhen(v)
  if (c.type === 'date' && typeof v === 'string') return calendarDay(v)
  if (c.type === 'enum' && typeof v === 'string') return codeText(v, c.table, c.field)
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export const isLongValue = (from, to) => valueText(from).length + valueText(to).length > 90

// One side of a field row: a reference column's value is the record it
// names (content's fromRef/toRef: a role's name, a member linked as a
// person); any other value is its text, by its type. when: a time's own
// value, for its exact UTC hover.
const sideView = (value, ref, opts, c = {}) => {
  if (!ref) {
    const when = c.type === 'time' && typeof value === 'string' && !Number.isNaN(new Date(value).getTime()) ? value : null
    return { text: valueText(value, c), href: null, note: null, when }
  }
  const v = recordView(ref, opts)
  return { text: v.text, href: v.href, note: v.note, when: null }
}

// changeView: how one field row reads. An inserted value has only its new
// side, a removed one only what it held -- shown plainly, never "x →
// (empty)" -- and a masked one neither (owner bug report 2026-10-06). A
// masked value reads "changed" on an update (a secret was replaced); a
// creation or deletion never changed anything, so it reads as who it is
// (content's maskedAs: a staff member is EducationXR support) or "not shown".
const maskedText = c => (c && c.maskedAs) || (c && (c.op === 'INSERT' || c.op === 'DELETE') ? 'not shown' : 'changed')

export const changeView = (c, opts = {}) => {
  if (!c || c.masked) return { kind: 'masked', from: null, to: null, text: maskedText(c) }
  if (c.op === 'INSERT') return { kind: 'added', from: null, to: sideView(c.to, c.toRef, opts, c) }
  if (c.op === 'DELETE') return { kind: 'removed', from: sideView(c.from, c.fromRef, opts, c), to: null }
  return { kind: 'changed', from: sideView(c.from, c.fromRef, opts, c), to: sideView(c.to, c.toRef, opts, c) }
}

// The field rows the timeline shows: a creation or deletion lists at most
// three meaningful fields there, and content marks the rest detail (shown
// under Details, which lists every row); an update shows them all. An
// entry from a content that marks nothing shows every row, as before.
export const inlineChanges = entry => ((entry && entry.changes) || []).filter(c => !c.detail)

// The record is the actor's own login or membership (content's
// record.self, or the actor's own ids): the line does not repeat it -- "David
// Greco signed in with a launch code", not "... a launch code David Greco".
export const isSelfRecord = entry => {
  const r = entry && entry.record
  if (!r) return false
  if (r.self) return true
  const a = entry.actor
  if (!a || r.self === false) return false
  const same = (x, y) => x !== null && x !== undefined && String(x) === String(y)
  if (r.type === 'logins') return same(a.loginID, r.id)
  if (r.type === 'users') return same(a.userID, r.id)
  return false
}

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

export const dayKey = iso => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

export const dayLabel = (iso, now = new Date()) => {
  const d = new Date(iso)
  if (sameDay(d, now)) return 'Today'
  const y = new Date(now)
  y.setDate(y.getDate() - 1)
  if (sameDay(d, y)) return 'Yesterday'
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric'
  })
}

// A daily count's sentence ends "(once that day)", "(3 times that day)":
// under the TODAY and YESTERDAY headings that reads as "today" and
// "yesterday" (the same local days the timeline's headings use); older
// days keep "that day".
const THAT_DAY = / that day\)$/
export const daySentence = (sentence, iso, now = new Date()) => {
  if (!sentence || !THAT_DAY.test(sentence)) return sentence
  const label = dayLabel(iso, now)
  if (label !== 'Today' && label !== 'Yesterday') return sentence
  return sentence.replace(THAT_DAY, ` ${label.toLowerCase()})`)
}

// The precise UTC instant ("2026-10-08 17:20:05 UTC"): the hover of every
// time the trail shows (relative or the viewer's own).
export const exactUTC = iso => utcText(iso)

export const countryName = code => {
  if (!code) return ''
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code
  } catch (e) {
    return code
  }
}

export const locationText = loc => {
  if (!loc) return ''
  const place = [loc.city, loc.region, loc.country].filter(Boolean).join(', ')
  return [place, loc.network].filter(Boolean).join(' · ')
}

const TONES = {
  security: 'bad',
  sign_ins: 'info',
  people_access: 'ok',
  publishing: 'info',
  email: 'warn'
}
export const categoryTone = key => TONES[key] || ''

const CATEGORY_LABELS = {
  changes: 'Changes',
  publishing: 'Publishing',
  people_access: 'People & access',
  security: 'Security',
  sign_ins: 'Sign-ins',
  views: 'Views',
  email: 'Email'
}
export const categoryLabel = key => CATEGORY_LABELS[key] || key || ''

export const firstName = text => String(text || '').split(/\s+/)[0]
